import { api, ApiError } from "../api/client";

/**
 * AI work the API queues on the worker (the intake assessment, the board report, the AI
 * recommendation on a finding): the POST answers 202 with a job id; this polls the job's status
 * route and resolves with the body the call used to return. A refusal rejects with an ApiError
 * carrying the status and the readable message the API stored. The same shape as the policy
 * generation jobs (lib/generatePolicy.ts).
 */

/** How long a page waits before handing the wait back (the job keeps running; it can be resumed). */
export const AI_JOB_WAIT_MS = 10 * 60 * 1000;
const POLL_MS = 3000;

type JobState<R> =
  | { status: "queued" | "running"; queuedAt?: number }
  | ({ status: "done" } & R)
  | { status: "failed"; httpStatus?: number; error?: string; message?: string };

export interface AiJobOptions {
  /** Called with the job id once queued, so a page can remember it and resume after navigating away. */
  onJobId?: (jobId: string) => void;
  onStatus?: (s: { status: "queued" | "running"; queuedAt?: number }) => void;
  /** Stops waiting (the job itself carries on). */
  signal?: AbortSignal;
  waitMs?: number;
}

/** Queues the work at `startPath` and waits for it at `statusPath(jobId)`. */
export async function runAiJob<R extends object>(
  startPath: string,
  statusPath: (jobId: string) => string,
  body: unknown = {},
  options: AiJobOptions = {},
): Promise<R> {
  const { jobId } = await api.post<{ jobId: string }>(startPath, body);
  options.onJobId?.(jobId);
  return pollAiJob<R>(jobId, statusPath, options);
}

/** Waits for a job already queued (by this page, or before the user navigated away). */
export async function pollAiJob<R extends object>(
  jobId: string,
  statusPath: (jobId: string) => string,
  options: AiJobOptions = {},
): Promise<R> {
  const deadline = Date.now() + (options.waitMs ?? AI_JOB_WAIT_MS);
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, POLL_MS));
    if (options.signal?.aborted) throw new DOMException("Stopped waiting", "AbortError");
    let state: JobState<R>;
    try {
      state = await api.get<JobState<R>>(statusPath(jobId));
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) throw e;
      continue; // transient: keep waiting
    }
    if (options.signal?.aborted) throw new DOMException("Stopped waiting", "AbortError");
    if (state.status === "queued" || state.status === "running") {
      options.onStatus?.({ status: state.status, queuedAt: state.queuedAt });
      continue;
    }
    if (state.status === "done") {
      const { status: _status, ...rest } = state;
      return rest as unknown as R;
    }
    const failed = state as { httpStatus?: number; error?: string; message?: string };
    throw new ApiError(failed.httpStatus ?? 500, { error: failed.error ?? "ai_request_failed", message: failed.message });
  }
  throw new Error("This is taking longer than usual. It will be here when it is ready; check back shortly.");
}

// ── Resuming after navigating away ───────────────────────────────────────────
// A job id is not a secret (the status route only answers for the organisation that queued it),
// so it is kept in this browser to pick the job up again when the page is reopened.

const STORE_KEY = "abacas.aiJobs";
interface Remembered { jobId: string; startedAt: number }

function readStore(): Record<string, Remembered> {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) ?? "{}") as Record<string, Remembered>; } catch { return {}; }
}
function writeStore(map: Record<string, Remembered>): void {
  try {
    if (Object.keys(map).length === 0) localStorage.removeItem(STORE_KEY);
    else localStorage.setItem(STORE_KEY, JSON.stringify(map));
  } catch { /* storage unavailable: the page just cannot resume */ }
}

export function rememberAiJob(key: string, jobId: string): void {
  writeStore({ ...readStore(), [key]: { jobId, startedAt: Date.now() } });
}
export function forgetAiJob(key: string): void {
  const map = readStore();
  delete map[key];
  writeStore(map);
}
/** Jobs remembered under keys starting with `prefix` that are still inside the wait. */
export function recalledAiJobs(prefix: string): Array<{ key: string; jobId: string; remainingMs: number }> {
  const now = Date.now();
  const out: Array<{ key: string; jobId: string; remainingMs: number }> = [];
  for (const [key, v] of Object.entries(readStore())) {
    if (!key.startsWith(prefix)) continue;
    const remainingMs = AI_JOB_WAIT_MS - (now - v.startedAt);
    if (remainingMs > 0) out.push({ key, jobId: v.jobId, remainingMs });
    else forgetAiJob(key);
  }
  return out;
}
